import { G } from '@mobily/ts-belt';
import { assign, createMachine, enqueueActions } from 'xstate';
import type { ReportSelectEventData } from '@stitch-fix/event-reporter';
import type { PaymentType } from '@stitch-fix/form-payments';
import { defaultFlow, type FlowId } from './flows';
import { browserLogger } from '../../../../utils/loggers/browser';
import { getFlowSteps, getSteps, persistStepData } from './utils';
import type { AnchoredFixScheduleInput, BusinessLine, FixScheduleInput, OnboardingStatus, SignUp } from '../../../../types/definitions/vendor/graphql-schema.d';
export type ScheduleFixEvent = Extract<ReportSelectEventData, {
  schema: 'schedule_fix';
}>;
export type FlowContext = {
  flow: FlowId;
  currentStepIndex: number;
  nextStepPath?: string;
  previousStepPath?: string;
  data?: FlowData;
  errorMessage?: string;
  anchorFixOutOfStock?: boolean;
  navTitle?: string;
  businessLine: BusinessLine;
  isRemoveItemCountEnabled?: boolean;
  email?: string;
  paymentType?: PaymentType;
  fixSize: number;
  signUpType: SignUp;
  visionOnboardingStatus?: OnboardingStatus;
};
export type FlowData = Partial<FixScheduleInput> & Partial<AnchoredFixScheduleInput>;
type FlowEvent = {
  type: 'BACK';
} | {
  type: 'NEXT';
  data?: FlowData;
} | {
  type: 'RESET_FLOW';
  flow: FlowId;
  stepIndex: number;
  data: FlowData;
  businessLine: BusinessLine;
  email: string;
  fixSize: number;
  isRemoveItemCountEnabled: boolean;
  signUpType: SignUp;
  visionOnboardingStatus?: OnboardingStatus;
} | {
  type: 'PAYMENT_ERROR_REDIRECT';
  message: string;
} | {
  type: 'UPDATE_FLOW_STEPS';
  currentStepIndex: number;
} | {
  type: 'UPDATE_NAV_TITLE';
  title?: string;
} | {
  type: 'UPDATE_PAYMENT_TYPE';
  paymentType: PaymentType;
} | {
  type: 'SET_ANCHOR_FIX_OUT_OF_STOCK';
};
export type FlowMachineInput = Partial<FlowContext>;
export const flowMachine = createMachine({
  types: {} as {
    context: FlowContext;
    events: FlowEvent;
    input: FlowMachineInput;
  },
  /** @xstate-layout N4IgpgJg5mDOIC5QDMA2B7A7gOgJYVTAGIAhAQQGEBpAbQAYBdRUAB3VlwBdd0A7ZkAA9EAWgBMYgKzYAbAA46kgJwyALAGYxcpWIDsAGhABPUZLrYAjDJm7VY1arkWl6yapkBfD4bRY8BYgA5AFEADQAVeiYkEDYObj4BYQQRVQt1bF06ZwklOjF1C10ZQxMUyTlsJzMdG3Us1V1dSS8fDBx8QiIAJWCAZWDwgH0AMQAZAHkAdSiBOK4efhjk8SULbHU5eWd1GQrGuVLRdXUlWSVJXR2LRSUNXVaQXw6AogBVAAUAETJw4NHJlMhn0-h8+rMYvMEktQCsVJYxEpqhY5FpaodjMdTudLtdbvdHs9-F0Iax2AtEstEFZzA46DItO4xHR1NkjilnGcTnJdijCrZ1A5Ce1sLwAIYAN1wUDF3F4UCIED4YDwvAl6AA1iqieKpTK5VAELg1egAMayxZRUmxcnQpKiKzrRwOPIVVFKZrskQWMTYNb3OSNH0XZlyYV+XXSi3yohgABOcfQcewLFQsuQSYAttgdZKowajSbzdCrYw5rbFvaUnpsJJTkVJD65Ao9Cp2ZIZJYlGssv66I1JB3wzhTehM6mwJxiB8yABNACywUCw2C3W6E26Q16XwAkr0KJEy5CK5TYYgxPJMkzJPZUQ31F76pZVHQ7q+roibxYvN4QLx0BAcACM85bxJWVIpO46xZDkiL5PyJSYpBNbOLiLg3MoBK-kSnRgKBFIwkIxwdrWGj2KoFzdiy7hehIGQ8jy1gyHQWSso4w6inm+rGlA+F2hBIi7JUDhNHQ9IWG4VgPkhzKqNgLqnIKg5ZPknjYSKo7joQU58eBZ4IIK6yNioeyOJoRTSWU4jZLWza7Io+R1i46g-h4QA */
  id: 'flow',
  initial: 'idle',
  context: ({
    input
  }) => ({
    flow: defaultFlow,
    currentStepIndex: 0,
    businessLine: 'WOMENS' as BusinessLine,
    fixSize: 0,
    signUpType: 'EMAIL' as SignUp,
    ...(input as FlowMachineInput | undefined)
  }),
  states: {
    idle: {
      description: 'Await user navigation events, or transition to complete state if final step.',
      always: {
        target: 'complete',
        guard: 'isFinalStep'
      },
      exit: 'clearError',
      on: {
        BACK: 'navigating',
        NEXT: {
          actions: ['assignDataToContext', 'updateCookie'],
          target: 'navigating'
        },
        RESET_FLOW: {
          actions: 'resetFlow'
        },
        UPDATE_FLOW_STEPS: {
          actions: 'assignStepsToContext'
        },
        UPDATE_NAV_TITLE: {
          actions: 'assignNavTitleToContext'
        },
        UPDATE_PAYMENT_TYPE: {
          actions: 'assignPaymentTypeToContext'
        },
        SET_ANCHOR_FIX_OUT_OF_STOCK: {
          actions: 'assignErrorMessageToContext'
        }
      }
    },
    navigating: {
      description: `Invoke the handleFlowNavigation service, which is defined as a config
          option in FlowProvider, and handles step transition animations and routing.`,
      invoke: {
        src: 'handleFlowNavigation',
        input: ({
          context,
          event
        }) => ({
          context,
          event
        }),
        onDone: {
          target: 'idle'
        },
        onError: {
          target: 'idle',
          actions: 'logError'
        }
      }
    },
    complete: {
      on: {
        SET_ANCHOR_FIX_OUT_OF_STOCK: {
          target: 'navigating',
          actions: ['assignErrorMessageToContext', 'assignPreviousStepAfterError']
        },
        PAYMENT_ERROR_REDIRECT: {
          target: 'navigating',
          actions: ['assignErrorMessageToContext', 'assignPreviousStepAfterError']
        }
      }
    }
  }
}, {
  guards: {
    isFinalStep: ({
      context
    }) => {
      return !G.isUndefined(context.flow) && context.currentStepIndex === getFlowSteps(context).length - 1;
    }
  },
  actions: {
    assignDataToContext: assign({
      data: ({
        context,
        event
      }) => ({
        ...context.data,
        ...(event.type === 'NEXT' && event.data)
      })
    }),
    assignErrorMessageToContext: assign({
      errorMessage: ({
        event
      }) => event.type === 'PAYMENT_ERROR_REDIRECT' ? event.message : undefined,
      anchorFixOutOfStock: ({
        event
      }) => event.type === 'SET_ANCHOR_FIX_OUT_OF_STOCK'
    }),
    assignPreviousStepAfterError: assign(({
      context,
      event
    }) => {
      // We need to set the context to the previous step when the user encounters a payment error
      if (event.type === 'PAYMENT_ERROR_REDIRECT' || event.type === 'SET_ANCHOR_FIX_OUT_OF_STOCK') {
        const previousStepIndex = Math.max(context.currentStepIndex - 1, 0);
        return {
          ...context,
          ...getSteps({
            ...context,
            currentStepIndex: previousStepIndex
          })
        };
      }
      return context;
    }),
    assignStepsToContext: assign(({
      context,
      event
    }) => {
      if (event.type === 'UPDATE_FLOW_STEPS') {
        return {
          ...context,
          ...getSteps({
            ...context,
            currentStepIndex: event.currentStepIndex
          })
        };
      }
      return context;
    }),
    assignNavTitleToContext: assign({
      navTitle: ({
        event
      }) => event.type === 'UPDATE_NAV_TITLE' ? event.title : undefined
    }),
    assignPaymentTypeToContext: assign({
      paymentType: ({
        event
      }) => event.type === 'UPDATE_PAYMENT_TYPE' ? event.paymentType : undefined
    }),
    clearError: enqueueActions(({
      context,
      enqueue
    }) => {
      if (!context.errorMessage) {
        return;
      }
      enqueue.assign({
        errorMessage: () => undefined
      });
    }),
    logError: ({
      event
    }) => browserLogger({
      level: 'error',
      feature: 'lower-fix-funnel',
      message: 'Lower Fix Funnel navigation error',
      context: {
        error: (event as unknown as {
          error: unknown;
        }).error
      }
    }),
    resetFlow: assign(({
      context,
      event
    }) => {
      if (event.type === 'RESET_FLOW') {
        const {
          flow,
          stepIndex,
          data,
          businessLine,
          email,
          fixSize,
          isRemoveItemCountEnabled,
          signUpType,
          visionOnboardingStatus
        } = event;
        const updatedContext = {
          flow,
          currentStepIndex: stepIndex,
          data,
          businessLine,
          email,
          fixSize,
          isRemoveItemCountEnabled,
          signUpType,
          visionOnboardingStatus
        };
        return {
          ...updatedContext,
          ...getSteps(updatedContext)
        };
      }
      return context;
    }),
    updateCookie: ({
      context
    }) => {
      persistStepData(context.data);
    }
  }
});